import {
  useDeleteTask,
  useDuplicateTasks,
  useTaskTodayControls,
} from 'features/tasks';
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  CheckCircle16,
  CopyDuplicateObject16,
  Trash16,
} from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import {
  Colors,
  PopupMenu,
  PopupMenuButton,
  PopupMenuList,
  PopupMenuListItem,
} from 'shared/components/ui/popup-menu';
import { InteractionType } from 'shared/types/data-type';
import { Task } from 'shared/types/task';

export type ActionTaskContextMenuProps = Omit<
  React.ComponentProps<typeof PopupMenu>,
  'children' | 'interactionType' | 'onOutside'
> & {
  task: Task;
  onClose: () => void;
  onAction?: () => void;
};

export const ActionTaskContextMenu: React.FC<ActionTaskContextMenuProps> = ({
  task,
  onClose,
  onAction,
  ...rest
}) => {
  const { t } = useTranslation();

  const { onComplete } = useTaskTodayControls();
  const { submit: deleteTask } = useDeleteTask();
  const duplicateTask = useDuplicateTasks();

  const _completeTask = () => {
    onComplete(task, undefined, { keepDetailOpen: true });
    onAction?.();
    onClose();
  };
  const _duplicateTask = () => {
    duplicateTask([task]);
    onAction?.();
    onClose();
  };
  const _deleteTask = () => {
    deleteTask(task);
    onAction?.();
    onClose();
  };

  return (
    <PopupMenu
      {...rest}
      interactionType={InteractionType.Task}
      onOutside={onClose}
    >
      <PopupMenuList hasBottomBorder>
        <PopupMenuListItem>
          <PopupMenuButton
            start={<Icon icon={CheckCircle16} />}
            onClick={_completeTask}
          >
            {t(
              task.completedAt
                ? 'context-menus.calendar-events.labels.goal.un-complete.label'
                : 'context-menus.calendar-events.labels.goal.complete.label',
            )}
          </PopupMenuButton>
        </PopupMenuListItem>
        <PopupMenuListItem>
          <PopupMenuButton
            start={<Icon icon={CopyDuplicateObject16} />}
            onClick={_duplicateTask}
          >
            {t('context-menus.calendar-events.labels.goal.duplicate.label')}
          </PopupMenuButton>
        </PopupMenuListItem>
      </PopupMenuList>
      <PopupMenuList>
        <PopupMenuListItem>
          <PopupMenuButton
            start={<Icon icon={Trash16} />}
            onClick={_deleteTask}
            startColor={Colors.Error}
            textColor={Colors.Error}
          >
            {t('context-menus.calendar-events.labels.goal.delete.label')}
          </PopupMenuButton>
        </PopupMenuListItem>
      </PopupMenuList>
    </PopupMenu>
  );
};
